import { Component, OnInit, ViewChild } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { DynamicSQLService } from '../../../../services/dynamic-sql.service'
import { ColDef, GridApi, GridReadyEvent, RowStyle, RowClassParams } from 'ag-grid-enterprise';
import { ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

@Component({
  selector: 'app-viewgrid',
  templateUrl: './viewgrid.component.html',
  styleUrls: ['./viewgrid.component.css']
})


export class ViewGridComponent implements OnInit {
  @ViewChild('agGrid') agGrid!: AgGridAngular;
  token!: string
  columnDefs: ColDef[] = [];
  rowData: any[] = [];
  gridApi!: GridApi;
  viewName!: string;
  sideBar = {
    toolPanels: [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
      },
      {
        id: 'filters',
        labelDefault: 'Filters',
        labelKey: 'filters',
        iconKey: 'filter',
        toolPanel: 'agFiltersToolPanel',
      },
    ],
    defaultToolPanel: 'columns',
  };
  rowGroupPanelShow: 'always' | 'onlyWhenGrouping' | 'never' = 'always';
  autoGroupColumnDef = {
    headerName: 'Group',
    width: 300,
    minWidth: 250,
    cellRendererParams: {
      suppressCount: false
    }
  };
  constructor(
    private route: ActivatedRoute,
    private dynamicSqlGridService: DynamicSQLService,
    private dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    const user: any = sessionStorage.getItem("wOSUser");
    if (user) {
      this.token = JSON.parse(user).stsTokenManager.accessToken;

      this.route.paramMap.subscribe(params => {
        const view = params.get('view');
        if (view) {
          this.viewName = view;
          this.loadViewData(view);
        } else {
          console.error('No view parameter provided');
        }
      });
    } else {
      console.error('No wOSUser found in sessionStorage');
    }
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
  }

  loadViewData(view: string): void {
    this.dynamicSqlGridService.getViewData(view, this.token).subscribe({
      next: (response: { data: any[] }) => {
        if (!response?.data?.length) {
          console.warn('No data available for view:', view);
          this.rowData = [];
          this.columnDefs = [];
          return;
        }
        this.rowData = response.data as any[];
        const ROW_GROUPS = new Set(['Client', 'Task']);
        const PIVOT_COL_HEADER = 'Entry Month';
        const PIVOT_COL_FIELD = 'entry_month';
        const VALUE_AGGS: Record<string, 'sum' | 'avg' | 'min' | 'max' | 'count'> = {
          Amount: 'sum',
        };
        const WIDTHS: Record<string, number> = {
          Client: 200, Category: 150, Task: 200, Date: 140,
          Resource: 150, Description: 300, Hours: 100, Rate: 100,
          Discount: 100, Amount: 120,
          'Entry Month': 140, entry_month: 140,
        };

        this.columnDefs = Object.keys(this.rowData[0]).map((fieldKey) => {
          const header = this.formatHeader(fieldKey);
          const col: ColDef = {
            headerName: header,
            field: fieldKey,
            sortable: true,
            filter: true,
            resizable: true,
            editable: false,
            enableRowGroup: true,
            enablePivot: true,
            enableValue: true,
          };
          if (ROW_GROUPS.has(header) || ROW_GROUPS.has(fieldKey)) {
            col.rowGroup = true;
          }
          const agg = VALUE_AGGS[header] || VALUE_AGGS[fieldKey];
          if (agg) col.aggFunc = agg;
          const isPivotMatch =
            header === PIVOT_COL_HEADER || fieldKey === PIVOT_COL_FIELD;
          if (isPivotMatch) col.pivot = true;
          const width = WIDTHS[header] ?? WIDTHS[fieldKey];
          if (width) col.width = width;
          return col;
        });
        setTimeout(() => this.gridApi?.sizeColumnsToFit(), 0);
      },
      error: (err) => console.error('Error fetching view data:', err)
    });
  }

  private formatHeader(key: string): string {
    return key.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
  }

  getRowStyle = (params: RowClassParams): RowStyle | undefined => {
    if (params.node.group) {
      return undefined;
    }
    return undefined;
  };

  exportToCSV(): void {
    if (!this.gridApi) {
      console.error('Grid API is not initialized');
      return;
    }
    this.gridApi.exportDataAsExcel({
      fileName: 'grid-export.xlsx',
      allColumns: false,
      onlySelected: false,
      sheetName: 'Grid Data',
      columnWidth: 100,
    });
  }

  exportToPDF(): void {
    if (!this.gridApi || !this.columnDefs || this.columnDefs.length === 0) {
      console.error('Grid API or column definitions not initialized');
      return;
    }
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFont('times');
    const columns = this.columnDefs.map(col => ({
      headerName: col.headerName || col.field,
      field: col.field!,
    }));
    const rows: any[] = [];
    let totalHours = 0;
    let totalAmount = 0;
    this.gridApi.forEachNodeAfterFilter((node) => {
      totalHours += parseFloat(node.data.Hours) || 0;
      totalAmount += parseFloat(node.data.Amount) || 0;
    });
    const totalRow = columns.map((col) => {
      if (col.field === 'Task') return 'Total';
      if (col.field === 'Hours') return totalHours.toFixed(2);
      if (col.field === 'Amount') return `$${Math.ceil(totalAmount).toLocaleString()}`;
      return '';
    });
    rows.push(totalRow);
    this.gridApi.forEachNodeAfterFilterAndSort((node) => {
      const row = columns.map(col => node.data[col.field] ?? '');
      rows.push(row);
    });
    let client = 'All Clients';
    this.gridApi.forEachNodeAfterFilter((node) => {
      if (node.data.Client) {
        client = node.data.Client;
        return;
      }
    });
    const imgWidth = 140;
    const imgHeight = 40;
    const imgX = 15;
    const imgY = 15;
    const pdfBannerImg = 'assets/banner2.png';
    const titleX = imgX + imgWidth + 10;
    const titleY = imgY + imgHeight / 2;
    const dateY = titleY + 10;
    autoTable(doc, {
      head: [columns.map(col => col.headerName ?? col.field)],
      body: rows,
      margin: { top: 80, bottom: 20 },
      theme: 'plain',
      didDrawPage: function (data: any) {
        if (data.pageNumber === 1) {
          doc.addImage(pdfBannerImg, 'PNG', imgX, imgY, imgWidth, imgHeight);
          doc.setFontSize(14);
          doc.setFont('times', 'bold');
          doc.text(`Consulting Details: ${client}`, titleX, titleY);
          doc.setFontSize(10);
          doc.setFont('times', 'normal');
          doc.text(`Created on: ${new Date().toLocaleDateString()}`, titleX, dateY);
          data.settings.margin.top = 20;
        }
      },
      styles: {
        font: 'times',
        fontSize: 10,
        cellPadding: 1,
      },
      headStyles: {
        fontStyle: 'bold',
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0]
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      showHead: 'everyPage',
      rowPageBreak: 'avoid',
    });

    const pageCount = doc.internal.pages.length - 1;
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width / 2, doc.internal.pageSize.height - 10, {
        align: 'center'
      });
    }
    doc.save(`ConsultingDetails_${client}.pdf`);
  }
}